import { formatInTimeZone } from 'date-fns-tz';
import { supabase } from '../lib/supabaseClient';
import {
  AUTORIZACIONES_DE_AUSENCIA, esCierreDeEjemplo, estadoDelCierre,
} from '../domain/impulso';
import type { CierreDeTurno, EstadoDeCierre } from '../domain/impulso';
import { skusVigentesEn } from '../domain/catalogoDeCierre';
import { listTurnosParaReporte } from './positions';
import { listApprovedAuthorizations } from './authorizations';
import { completarTrabajadores } from './trabajadoresDeMisTiendas';

/** Un producto del catálogo de la campaña (`impulso_skus`, migración 0090). */
export interface SkuDeImpulso {
  code: string;
  label: string;
  linea: string;
  /** Desde y hasta qué día se pregunta (0323). Sin fecha, no tiene ese límite. */
  vigente_desde?: string | null;
  vigente_hasta?: string | null;
}

/** El catálogo de la campaña, ordenado como se muestra. */
/** Una campaña que tiene formulario de cierre, para el banco de pruebas de oficina. */
export interface CampaniaDeImpulso {
  id: string;
  name: string;
  /** El cliente decide el cuestionario de P&G (28-oct). */
  client: string;
  brand: string;
  cierreConCanje: boolean;
}

/**
 * Las campañas activas que tienen productos cargados, o sea las que tienen cierre de turno.
 *
 * Existe para que quien administra pueda revisar el formulario de cualquiera sin fabricarse
 * una asignación real en esa campaña. La RLS decide qué campañas ve cada quien; acá no se
 * filtra por rol, se pide y el servidor contesta lo que corresponde.
 */
export async function listCampaniasDeImpulso(): Promise<CampaniaDeImpulso[]> {
  const { data, error } = await supabase
    .from('campaigns')
    .select('id, name, client, brand, cierre_con_canje, impulso_skus!inner(id)')
    .is('archived_at', null)
    .eq('impulso_skus.active', true)
    .order('brand');
  if (error) throw new Error(error.message);
  const vistas = new Map<string, CampaniaDeImpulso>();
  for (const c of (data ?? []) as unknown as
       { id: string; name: string; client: string; brand: string; cierre_con_canje: boolean }[]) {
    if (!vistas.has(c.id)) {
      vistas.set(c.id, {
        id: c.id, name: c.name, client: c.client, brand: c.brand, cierreConCanje: c.cierre_con_canje === true,
      });
    }
  }
  return [...vistas.values()];
}

/**
 * El catálogo de la campaña, en el orden en que se muestra.
 *
 * Con `fecha`, solo lo que se pregunta ese día: es lo que usa el celular. Sin `fecha`, el catálogo entero
 * con los productos que ya salieron (los que tienen `vigente_hasta`), para que la bandeja muestre cada
 * cierre con lo que se preguntó su día (`skusVigentesEn`). Un producto apagado sin fecha de salida no
 * vuelve: es como estaban los catálogos antes de la 0323.
 */
export async function listSkusDeImpulso(campaignId: string, fecha?: string): Promise<SkuDeImpulso[]> {
  const { data, error } = await supabase
    .from('impulso_skus')
    .select('code, label, linea, vigente_desde, vigente_hasta')
    .eq('campaign_id', campaignId)
    .or('active.eq.true,vigente_hasta.not.is.null')
    .order('sort');
  if (error) throw new Error(`No se pudo cargar el catálogo de productos: ${error.message}`);
  const catalogo = (data ?? []) as SkuDeImpulso[];
  return fecha ? skusVigentesEn(catalogo, fecha) : catalogo;
}

/**
 * ¿Ya registró su cierre de ese día? La regla es uno por persona, campaña y día.
 *
 * ---------------------------------------------------------------------------
 * 🔴 UN REGISTRO DE EJEMPLO NO PUEDE BLOQUEAR A UNA PERSONA REAL
 * ---------------------------------------------------------------------------
 * Esta función decide si el formulario del celular se muestra o no: en
 * `movil/CierreDeTurnoForm.tsx` el cuerpo entero está detrás de `{turno && !yaEsta && …}`, así que
 * cuando devuelve `true` la impulsadora no ve campos ni botón — solo el aviso «Ya registraste tu
 * cierre de esa fecha».
 *
 * Y la clave es **persona + campaña + fecha**, NO la asignación. Eso lo volvió un arma: dos cierres
 * sembrados por SQL para armar el tablero llevaban el `worker_id` de dos impulsadoras reales, así
 * que el 05/09 el sistema **les tapó el formulario y encima les afirmó que ya habían reportado**.
 * No es que no quisieron: no tenían dónde escribir. Y el tablero del cliente mostró esos dos días
 * como «asistió y no reportó», o sea que parecía que ellas habían fallado.
 *
 * Afectó a Lady Clarissa Herrera (Makro Surco) y a Marilyn Huanca (Makro Arequipa), las dos el
 * 2026-09-05. Lo encontró el usuario preguntando *«entonces al recurso cuando quiso subir su
 * cierre, no pudo?»* — yo tenía los dos registros a la vista desde antes y los había tratado como
 * una diferencia de 43 unidades entre dos pantallas.
 *
 * Por eso los de EJEMPLO se excluyen acá. Es defensa en profundidad y no un parche: aunque se
 * borren esas dos filas, la próxima vez que alguien siembre datos para una demo, el filtro ya está
 * puesto y ninguna persona real se queda sin poder reportar.
 *
 * ⚠️ Lo que SÍ tiene que seguir bloqueando es un cierre real, incluido el que completó la oficina
 * (`origen = 'oficina'`): ese es un registro legítimo de su día, y si ella manda otro quedarían dos
 * versiones del mismo turno conviviendo.
 */
export async function yaRegistroCierre(campaignId: string, fecha: string): Promise<boolean> {
  const { data: sesion } = await supabase.auth.getUser();
  const uid = sesion.user?.id;
  if (!uid) return false;
  const { data, error } = await supabase
    .from('impulso_reports')
    .select('ejecucion_comentario, competencia_comentario, incidencia_detalle')
    .eq('campaign_id', campaignId)
    .eq('fecha', fecha)
    .eq('worker_id', uid);
  // Un error de red no puede dejarla sin formulario: ante la duda, se le muestra. El servidor
  // vuelve a decidir al enviar, así que lo peor que pasa es un rechazo con mensaje claro.
  if (error) return false;
  type Raw = {
    ejecucion_comentario: string | null;
    competencia_comentario: string | null;
    incidencia_detalle: string | null;
  };
  return ((data ?? []) as Raw[]).some((r) => !esCierreDeEjemplo(
    r.ejecucion_comentario, r.competencia_comentario, r.incidencia_detalle,
  ));
}

/**
 * Registra el cierre de turno.
 *
 * `client_uuid` lo pone el cliente y la RPC lo usa para no duplicar: si la respuesta se pierde y
 * la persona reintenta, el servidor devuelve el mismo registro en vez de crear otro. Es la misma
 * idea que ya usa el canje, y en campo importa: se registra de noche, con señal mala.
 */
export async function registrarCierreDeTurno(cierre: CierreDeTurno): Promise<string> {
  const { data, error } = await supabase.rpc('registrar_impulso', {
    p_payload: {
      ...cierre,
      client_uuid: crypto.randomUUID(),
      ventas: cierre.ventas.filter((v) => v.unidades > 0),
      // La RPC ignora «ninguna» igual, pero mandarla sería mandar ruido: la ausencia de objeciones
      // ES «ninguna en particular», y una fila que diga eso no aporta nada al conteo.
      objeciones: cierre.objeciones
        .filter((o) => o.valor !== 'ninguna')
        .map((o) => ({ objecion: o.valor, comentario: o.comentario.trim() })),
    },
  });
  if (error) {
    // eslint-disable-next-line no-console -- la causa real, para poder depurar desde el celular
    console.error('registrar_impulso falló:', error);
    if (error.code === '23505') {
      throw new Error('Ya registraste tu cierre de esa fecha. Si necesitas corregirlo, avisa a tu supervisor.');
    }
    throw new Error(`No se pudo registrar tu cierre: ${error.message}`);
  }
  return data as string;
}

/** Una fila de la bandeja: el cierre de turno de alguien, con su total y sus banderas. */
export interface CierreEnBandeja {
  id: string;
  /**
   * La asignación de la que cuelga. **Puede ser null**, y eso no es teoría: al 8-set hay 3 cierres
   * así en producción. Es la clave con la que la bandeja los engancha a su posición.
   */
  assignmentId: string | null;
  /** Quién lo llenó: el celular de la impulsadora o alguien del panel (0099). */
  origen: 'celular' | 'oficina';
  /**
   * Registro SEMBRADO para armar una pantalla, no operación. Se reconoce por la palabra «EJEMPLO»
   * en sus textos, que es la convención con la que se crearon.
   *
   * No cuenta en ningún total de la bandeja, igual que no cuenta en el tablero del cliente. Se
   * sigue mostrando —con su etiqueta— para que se pueda revisar y borrar: esconderlo lo volvería
   * imposible de limpiar.
   */
  esEjemplo: boolean;
  /** El motivo, cuando lo completó la oficina. */
  notaOficina: string | null;
  /** Para cruzar el cierre con la fila del tablero de Hoy sin comparar nombres. */
  workerId: string | null;
  pdvId: string | null;
  /**
   * La campaña, para poder pedir SU catálogo de productos (`impulso_skus`).
   *
   * Sin esto la bandeja solo tiene los CÓDIGOS de lo vendido —`rap_00`, `tos_clasicas`— y los
   * imprimía tal cual. Con el catálogo se muestran el nombre legible, la línea a la que pertenece
   * y los productos que quedaron en cero, que hoy ni siquiera se guardan.
   */
  campaignId: string | null;
  fecha: string;
  registradoEn: string;
  trabajador: string;
  documento: string;
  tienda: string;
  campania: string;
  unidades: number;
  porSku: { sku: string; unidades: number }[];
  quiebre: string;
  quiebreSkus: string[];
  exhibicion: string;
  precioReferencia: number | null;
  ejecucionComentario: string | null;
  competencia: string;
  competenciaCual: string | null;
  competenciaExhibicion: string | null;
  competenciaPrecio: number | null;
  competenciaComentario: string | null;
  objeciones: { valor: string; comentario: string | null }[];
  incidencia: string;
  incidenciaDetalle: string | null;
  registroTardio: boolean;
  sinAsistencia: boolean;
  fueraDeProgramacion: boolean;
}

/**
 * Los cierres de un rango. La RLS decide qué filas llegan: la impulsadora ve las suyas, el equipo
 * de la campaña las de su gente, el jefe todas.
 */
export async function listCierresDeTurno(
  desde: string, hasta: string, campaignId?: string,
): Promise<CierreEnBandeja[]> {
  let q = supabase
    .from('impulso_reports')
    .select(
      'id, fecha, registrado_en, worker_id, pdv_id, campaign_id, assignment_id, origen, nota_oficina,'
      + ' quiebre, quiebre_skus, exhibicion_adicional, precio_referencia,'
      + ' ejecucion_comentario, competencia_impulsadora, competencia_cual, competencia_exhibicion,'
      + ' competencia_precio, competencia_comentario, objecion, incidencia, incidencia_detalle,'
      + ' registro_tardio, sin_asistencia, fuera_de_programacion,'
      + ' objeciones:impulso_objeciones(objecion, comentario),'
      + ' worker:profiles!impulso_reports_worker_id_fkey(first_names, last_names, dni),'
      + ' pdv:pdv(name), campaign:campaigns(client, brand),'
      + ' lineas:impulso_report_lines(sku_code, unidades)',
    )
    .gte('fecha', desde)
    .lte('fecha', hasta)
    .order('fecha', { ascending: false });
  if (campaignId) q = q.eq('campaign_id', campaignId);
  const { data, error } = await q;
  if (error) throw new Error(`No se pudieron cargar los cierres: ${error.message}`);
  type Raw = {
    id: string; fecha: string; registrado_en: string;
    worker_id: string | null; pdv_id: string | null; campaign_id: string | null;
    assignment_id: string | null; origen: 'celular' | 'oficina'; nota_oficina: string | null;
    quiebre: string; quiebre_skus: string[]; exhibicion_adicional: string;
    precio_referencia: number | string | null; ejecucion_comentario: string | null;
    competencia_impulsadora: string; competencia_cual: string | null;
    competencia_exhibicion: string | null; competencia_precio: number | string | null;
    competencia_comentario: string | null; objecion: string;
    objeciones: { objecion: string; comentario: string | null }[] | null;
    incidencia: string; incidencia_detalle: string | null;
    registro_tardio: boolean; sin_asistencia: boolean; fuera_de_programacion: boolean;
    worker: { first_names: string; last_names: string; dni: string } | null;
    pdv: { name: string } | null;
    campaign: { client: string; brand: string } | null;
    lineas: { sku_code: string; unidades: number }[] | null;
  };
  const rows = (data ?? []) as unknown as Raw[];
  // El supervisor no lee `profiles` (0218): para él el embed llega en null y sin esto la bandeja
  // diría «—» en cada fila. Se rellena por `worker_id` antes de mapear; para quien sí lee
  // `profiles` no hay huecos y no se consulta nada más.
  await completarTrabajadores(rows);
  return rows.map((r) => {
    const lineas = r.lineas ?? [];
    return {
      id: r.id,
      assignmentId: r.assignment_id,
      // `?? 'celular'` y no un error: la 0099 puso el default, pero si alguna vez se lee esta
      // tabla desde una copia anterior a la migracion, un cierre sin origen ES del celular.
      origen: r.origen ?? 'celular',
      esEjemplo: esCierreDeEjemplo(
        r.ejecucion_comentario, r.competencia_comentario, r.incidencia_detalle,
        ...(r.objeciones ?? []).map((o) => o.comentario),
      ),
      notaOficina: r.nota_oficina,
      workerId: r.worker_id,
      pdvId: r.pdv_id,
      campaignId: r.campaign_id,
      fecha: r.fecha,
      registradoEn: r.registrado_en,
      trabajador: r.worker ? `${r.worker.last_names} ${r.worker.first_names}` : '—',
      documento: r.worker?.dni || '—',
      tienda: r.pdv?.name ?? '—',
      campania: r.campaign ? `${r.campaign.client} — ${r.campaign.brand}` : '—',
      unidades: lineas.reduce((s, l) => s + Number(l.unidades), 0),
      porSku: lineas.map((l) => ({ sku: l.sku_code, unidades: Number(l.unidades) })),
      quiebre: r.quiebre,
      quiebreSkus: r.quiebre_skus ?? [],
      exhibicion: r.exhibicion_adicional,
      precioReferencia: r.precio_referencia === null ? null : Number(r.precio_referencia),
      ejecucionComentario: r.ejecucion_comentario,
      competencia: r.competencia_impulsadora,
      competenciaCual: r.competencia_cual,
      competenciaExhibicion: r.competencia_exhibicion,
      competenciaPrecio: r.competencia_precio === null ? null : Number(r.competencia_precio),
      competenciaComentario: r.competencia_comentario,
      objeciones: (r.objeciones ?? []).map((o) => ({ valor: o.objecion, comentario: o.comentario })),
      incidencia: r.incidencia,
      incidenciaDetalle: r.incidencia_detalle,
      registroTardio: r.registro_tardio,
      sinAsistencia: r.sin_asistencia,
      fueraDeProgramacion: r.fuera_de_programacion,
    };
  });
}

/** Qué dinámicas tiene una campaña. Decide qué botones ve la persona en su turno. */
export interface DinamicasDeCampania {
  /** Tiene dinámica de CANJE: se registra la compra de un cliente. */
  canje: boolean;
  /** Tiene dinámica de IMPULSO: se reporta el cierre de turno. */
  impulso: boolean;
  /**
   * Recorre PUESTOS dentro de un mercado: se registran visitas a comerciante y ventas con ruleta.
   * Softys · Toallitas Húmedas, desde el 14-set. Sale de `campaigns.visitas_puesto`, que es una
   * bandera y no una tabla de configuración porque acá no hay nada que configurar.
   */
  visitasPuesto: boolean;
}

/**
 * Las dinámicas activas de una campaña.
 *
 * Existe porque los botones del circuito NO pueden ser fijos. Bimbo Fijas es impulso puro —no hay
 * canje que registrar— y ofrecerle «Registrar canje» a una impulsadora de Bimbo la manda a una
 * pantalla vacía que no le corresponde. Al revés pasa lo mismo: en Makro, que es de canje, el
 * botón de cierre de turno no tiene nada que recoger.
 */
export async function dinamicasDeCampania(campaignId: string): Promise<DinamicasDeCampania> {
  const [canje, impulso, campana] = await Promise.all([
    supabase.from('campaign_dynamics').select('id')
      .eq('campaign_id', campaignId).eq('active', true).limit(1),
    supabase.from('impulso_skus').select('id')
      .eq('campaign_id', campaignId).eq('active', true).limit(1),
    supabase.from('campaigns').select('visitas_puesto').eq('id', campaignId).maybeSingle(),
  ]);
  return {
    canje: (canje.data ?? []).length > 0,
    impulso: (impulso.data ?? []).length > 0,
    visitasPuesto: campana.data?.visitas_puesto === true,
  };
}

/**
 * Las dinámicas de VARIAS campañas, unidas.
 *
 * Una persona puede trabajar dos campañas el mismo día y cada una trae lo suyo: la de canje
 * necesita el botón de canjes y la de impulso el de cierre de turno. Se piden las dos y se
 * suman, porque quitarle un botón a alguien que sí lo necesita la deja sin forma de reportar.
 * Se quitan los repetidos antes de preguntar: lo normal es que los dos turnos sean de la misma.
 */
export async function dinamicasDeLasCampanias(
  campaignIds: readonly string[],
): Promise<DinamicasDeCampania> {
  const unicas = [...new Set(campaignIds.filter(Boolean))];
  if (unicas.length === 0) return { canje: false, impulso: false, visitasPuesto: false };
  const todas = await Promise.all(unicas.map((id) => dinamicasDeCampania(id)));
  return {
    canje: todas.some((d) => d.canje),
    impulso: todas.some((d) => d.impulso),
    visitasPuesto: todas.some((d) => d.visitasPuesto),
  };
}

/** Mis últimos cierres, para que la persona vea lo que ya respondió. */
export interface MiCierre {
  id: string;
  fecha: string;
  registradoEn: string;
  tienda: string;
  unidades: number;
}

export async function listMisCierres(limite = 10): Promise<MiCierre[]> {
  const { data: sesion } = await supabase.auth.getUser();
  const uid = sesion.user?.id;
  if (!uid) return [];
  const { data, error } = await supabase
    .from('impulso_reports')
    .select('id, fecha, registrado_en, pdv:pdv(name), lineas:impulso_report_lines(unidades)')
    .eq('worker_id', uid)
    .order('fecha', { ascending: false })
    .limit(limite);
  if (error) throw new Error(`No se pudo cargar tu historial: ${error.message}`);
  type Raw = {
    id: string; fecha: string; registrado_en: string;
    pdv: { name: string } | null; lineas: { unidades: number }[] | null;
  };
  return ((data ?? []) as unknown as Raw[]).map((r) => ({
    id: r.id,
    fecha: r.fecha,
    registradoEn: r.registrado_en,
    tienda: r.pdv?.name ?? '—',
    unidades: (r.lineas ?? []).reduce((s, l) => s + Number(l.unidades), 0),
  }));
}

/* ==========================================================================
   La bandeja manejada por POSICIONES (0099)
   ========================================================================== */

/**
 * Una fila de la bandeja nueva: un turno asignado, con su cierre si lo tiene.
 *
 * El cambio de eje es lo importante. `listCierresDeTurno` parte de `impulso_reports`, asi que solo
 * puede mostrar lo que existe — y el usuario pidio exactamente lo contrario: *«ver de los asientos
 * asignados quienes faltan llenar cierres de turno»*. Una fila que no existe no se lista, asi que
 * la bandeja tenia que partir de las POSICIONES, como ya hace `Validacion.tsx`.
 */
export interface FilaDeCierre {
  /** Estable entre recargas: la clave de la posicion. */
  key: string;
  fecha: string;
  horario: string;
  tienda: string;
  /** Null cuando la posicion no tiene a nadie: sin esto no hay a quien pedirle el cierre. */
  assignmentId: string | null;
  workerId: string | null;
  trabajador: string;
  documento: string;
  estado: EstadoDeCierre;
  /** El cierre, cuando existe. */
  cierre: CierreEnBandeja | null;
  /** Cuantas correcciones tiene. Mayor que cero = editado, y asi se rotula. */
  correcciones: number;
}

/** Lo que devuelve la bandeja: los turnos, y aparte los cierres que no cuelgan de ninguno. */
export interface BandejaDeCierres {
  filas: FilaDeCierre[];
  /**
   * Cierres que no se pudieron enganchar a ninguna posicion del rango.
   *
   * NO se descartan, y es deliberado: al 8-set hay 3 cierres sin `assignment_id` en produccion y
   * uno de ellos no tiene ninguna posicion que lo explique. Una bandeja que dice ser la lista
   * completa y tira filas en silencio es peor que la que habia, porque nadie sabe que faltan.
   */
  huerfanos: CierreEnBandeja[];
}

/**
 * Los turnos asignados de una campania en un rango, con el estado de su cierre.
 *
 * La campania es OBLIGATORIA, a diferencia de la bandeja anterior que permitia «Todas». No es un
 * recorte gratuito: enumerar posiciones asignadas de todas las campanias a la vez son miles de
 * filas —P&G sola tiene 3.344— y el cierre de turno solo existe donde hay catalogo de productos.
 * Sin campania, la pregunta «¿quien no lleno su cierre?» no tiene respuesta bien definida.
 */
export async function listFilasDeCierre(
  campaignId: string, desde: string, hasta: string,
): Promise<BandejaDeCierres> {
  const hoy = formatInTimeZone(new Date(), 'America/Lima', 'yyyy-MM-dd');
  const [turnos, cierres] = await Promise.all([
    listTurnosParaReporte(campaignId, desde, hasta),
    listCierresDeTurno(desde, hasta, campaignId),
  ]);

  // Las justificaciones aprobadas son lo que separa una ausencia de un olvido. Se piden solo para
  // la gente del rango: `listApprovedAuthorizations` con [] devuelve [] sin consultar nada.
  const workerIds = [...new Set(turnos.map((t) => t.worker?.id).filter((x): x is string => !!x))];
  const permisos = await listApprovedAuthorizations(workerIds, desde, hasta);
  // Solo los tipos que significan que NO trabajo. `tardanza` dice lo contrario —llego tarde, o sea
  // que fue— y contarla como ausencia rotulo «Ausencia» un turno trabajado de 09:55 a 18:09.
  const conPermiso = new Set(
    permisos
      .filter((x) => AUTORIZACIONES_DE_AUSENCIA.includes(x.kind))
      .map((x) => `${x.worker_id}|${x.event_date}`),
  );

  // El enganche es por asignacion, y por (recurso, fecha) como respaldo. El respaldo NO es adorno:
  // recupera 2 de los 3 cierres que hoy no tienen `assignment_id`.
  const porAsignacion = new Map<string, CierreEnBandeja>();
  const porRecursoFecha = new Map<string, CierreEnBandeja>();
  for (const c of cierres) {
    if (c.assignmentId) porAsignacion.set(c.assignmentId, c);
    if (c.workerId) porRecursoFecha.set(`${c.workerId}|${c.fecha}`, c);
  }

  const correcciones = await contarCorreccionesDeCierre(cierres.map((c) => c.id));
  const usados = new Set<string>();

  const filas: FilaDeCierre[] = turnos.map((t) => {
    const clave = t.worker ? `${t.worker.id}|${t.date}` : '';
    const cierre = (t.assignmentId ? porAsignacion.get(t.assignmentId) : undefined)
      ?? (clave ? porRecursoFecha.get(clave) : undefined)
      ?? null;
    if (cierre) usados.add(cierre.id);
    return {
      key: t.key,
      fecha: t.date,
      horario: `${(t.start_time ?? '').slice(0, 5) || '—'}–${(t.end_time ?? '').slice(0, 5) || '—'}`,
      tienda: t.pdv?.name ?? '—',
      assignmentId: t.assignmentId,
      workerId: t.worker?.id ?? null,
      trabajador: t.worker ? `${t.worker.last_names} ${t.worker.first_names}` : 'Sin asignar',
      documento: t.worker?.dni || '—',
      estado: estadoDelCierre({
        fecha: t.date,
        hoy,
        tieneAsignacion: t.assignmentId !== null,
        tieneIngreso: t.ingreso !== null,
        tieneSalida: t.salida !== null,
        tieneAusencia: clave !== '' && conPermiso.has(clave),
        // Un registro de EJEMPLO NO es un cierre. Sin esto, la bandeja pinta «Con cierre» un turno
        // que el tablero del cliente muestra como «s/r», y las dos pantallas se contradicen sobre
        // el mismo dia. La fila igual trae el `cierre` para poder verlo y borrarlo.
        tieneCierre: cierre !== null && !cierre.esEjemplo,
      }),
      cierre,
      correcciones: cierre ? (correcciones.get(cierre.id) ?? 0) : 0,
    };
  });

  return { filas, huerfanos: cierres.filter((c) => !usados.has(c.id)) };
}

/**
 * Cuantas correcciones tiene cada cierre. Un cierre con filas aca es un cierre EDITADO.
 *
 * Se cuenta en lote y no una consulta por fila: con treinta cierres en pantalla —el caso normal—
 * eso serian treinta viajes para pintar una etiqueta.
 */
export async function contarCorreccionesDeCierre(ids: string[]): Promise<Map<string, number>> {
  const mapa = new Map<string, number>();
  if (ids.length === 0) return mapa;
  const { data, error } = await supabase
    .from('impulso_report_corrections')
    .select('report_id')
    .in('report_id', ids);
  // Se degrada a vacio en lugar de tumbar la bandeja: la etiqueta «editado» es informacion util,
  // no un requisito para poder trabajar. Es la misma decision que ya tomo la bandeja de canjes.
  if (error) return mapa;
  for (const r of (data ?? []) as { report_id: string }[]) {
    mapa.set(r.report_id, (mapa.get(r.report_id) ?? 0) + 1);
  }
  return mapa;
}

/** El rastro de un cierre, para mostrarlo en el detalle. */
export interface CorreccionDeCierre {
  campo: string;
  valorAnterior: string | null;
  valorNuevo: string | null;
  motivo: string;
  cuando: string;
  quien: string;
}

export async function listCorreccionesDeCierre(reportId: string): Promise<CorreccionDeCierre[]> {
  const { data, error } = await supabase
    .from('impulso_report_corrections')
    .select('campo, valor_anterior, valor_nuevo, motivo, created_at,'
      + ' actor:profiles!impulso_report_corrections_actor_id_fkey(first_names, last_names)')
    .eq('report_id', reportId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(`No se pudo cargar el historial: ${error.message}`);
  type Raw = {
    campo: string; valor_anterior: string | null; valor_nuevo: string | null;
    motivo: string; created_at: string;
    actor: { first_names: string; last_names: string } | null;
  };
  return ((data ?? []) as unknown as Raw[]).map((r) => ({
    campo: r.campo,
    valorAnterior: r.valor_anterior,
    valorNuevo: r.valor_nuevo,
    motivo: r.motivo,
    cuando: r.created_at,
    quien: r.actor ? `${r.actor.last_names} ${r.actor.first_names}` : '—',
  }));
}

/**
 * Completa desde el panel el cierre de un turno que no lo tiene.
 *
 * El PDV, el recurso y la fecha NO van en la carga: los saca la RPC de la asignacion. Es la misma
 * decision que tomo la 0076 con la asistencia manual, y por la misma razon: un formulario que te
 * deja escribir a nombre de quien registras termina registrando a nombre de quien no fue.
 */
export async function registrarCierreManual(
  assignmentId: string, cierre: CierreDeTurno, motivo: string,
): Promise<string> {
  const { data, error } = await supabase.rpc('registrar_cierre_manual', {
    p_payload: {
      assignment_id: assignmentId,
      motivo,
      client_uuid: crypto.randomUUID(),
      quiebre: cierre.quiebre,
      quiebre_skus: cierre.quiebre_skus,
      exhibicion_adicional: cierre.exhibicion_adicional,
      precio_referencia: cierre.precio_referencia,
      ejecucion_comentario: cierre.ejecucion_comentario,
      competencia_impulsadora: cierre.competencia_impulsadora,
      competencia_cual: cierre.competencia_cual,
      competencia_exhibicion: cierre.competencia_exhibicion,
      competencia_precio: cierre.competencia_precio,
      competencia_comentario: cierre.competencia_comentario,
      incidencia: cierre.incidencia,
      incidencia_detalle: cierre.incidencia_detalle,
      ventas: cierre.ventas.filter((v) => v.unidades > 0),
      objeciones: cierre.objeciones
        .filter((o) => o.valor !== 'ninguna')
        .map((o) => ({ objecion: o.valor, comentario: o.comentario.trim() })),
    },
  });
  if (error) {
    if (error.code === '23505') {
      throw new Error('Ese turno ya tiene un cierre. Usa «Editar» en lugar de crear otro.');
    }
    throw new Error(error.message);
  }
  return data as string;
}

/**
 * Lo que puede cambiar una correccion.
 *
 * Espeja la lista blanca de `corregir_cierre`. Si aca entra un campo que alla no esta, la RPC lo
 * ignora en silencio y el usuario se queda creyendo que guardo algo.
 */
export interface CambiosDeCierre {
  quiebre?: string;
  quiebre_skus?: string[];
  exhibicion_adicional?: string;
  precio_referencia?: string;
  ejecucion_comentario?: string;
  competencia_impulsadora?: string;
  competencia_cual?: string;
  competencia_exhibicion?: string;
  competencia_precio?: string;
  competencia_comentario?: string;
  incidencia?: string;
  incidencia_detalle?: string;
  ventas?: { sku_code: string; unidades: number }[];
  objeciones?: { objecion: string; comentario: string }[];
}

/** Corrige un cierre existente dejando rastro por campo. El motivo es obligatorio. */
export async function corregirCierre(
  reportId: string, cambios: CambiosDeCierre, motivo: string,
): Promise<void> {
  const { error } = await supabase.rpc('corregir_cierre', {
    p_record: reportId,
    p_cambios: cambios,
    p_motivo: motivo,
  });
  if (error) throw new Error(error.message);
}
/**
 * Borra el registro de verdad. Exige motivo, que queda en la constancia interna de `audit_log`.
 *
 * Es borrado REAL y no un estado «anulado», por decision del usuario: *«preferiria borrado real
 * porque no deberia haber rastro, es error total, pero igual que quede documentado que se borro a
 * la interna»*. La constancia la deja un disparador `before delete` con la fila entera (0101), asi
 * que no depende de que la app se acuerde de escribirla.
 */
export async function borrarCierre(id: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('borrar_cierre', { p_id: id, p_motivo: motivo });
  if (error) throw new Error(error.message);
}
